import { useCallback, useEffect, useRef, useState } from "react";
import { add, format } from "date-fns";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTab,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import {
  cycleKey,
  DEFAULT_FOLLOW_UP_CYCLE,
  findFollowUpCyclePreset,
  FOLLOW_UP_CYCLE_PRESETS,
  formatUpdateCycle,
  formatUpdateCycleShort,
  updateCycleDuration,
  type UpdateCycle,
} from "#app/lib/project-updates";

export type StartFollowUpInput = { cycle: UpdateCycle };

/** The pulse the whole follow-up lifecycle is marked with. */
const UPDATE_ICON = "heart-pulse";

/** The one sentence under the title: what starting does for the Member,
 *  never which entity this is (the detail behind the dialog says that) or
 *  what state it was in. */
const START_FOLLOW_UP_DESCRIPTION =
  "You’ll be reminded to post updates until you end it.";

/**
 * "Start Follow up": the first of the lifecycle's three acts, a sibling of
 * Post update and End Follow up. Pre-answered — the cycle opens on the
 * inherited default or Every 2 weeks, so Enter starts it — under one sentence
 * that says what starting does.
 *
 * The cycle is a segmented control in ladder
 * order — six short words, one axis, one press — and the date it commits the
 * Member to is the footer's leading meta ("First update · Tue, Sep 29"), the
 * slot Post update's "Assign to me" uses: the consequence of a choice, not a
 * seventh choice, so it sits with the verb it qualifies rather than under the
 * track competing with it. Six is the ceiling for the segments at this width
 * (`lg`, 512px, shared with Post update); a seventh rung is a list, not a
 * narrower segment. On a phone the six segments do not fit, so the cycle is a
 * Select there and the date rides above the stacked footer buttons.
 *
 * Focus lands on the popup, not the first control: a ring already lit at open
 * read as a stray keyboard state.
 */
export function StartFollowUpDialog({
  open,
  onOpenChange,
  pastCount = 0,
  defaultCycle = DEFAULT_FOLLOW_UP_CYCLE,
  onStart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: "project";
  /** Closed Follow ups already on the target ("third time chasing this guy"). */
  pastCount?: number;
  defaultCycle?: UpdateCycle;
  onStart: (input: StartFollowUpInput) => void;
}) {
  const [cycle, setCycle] = useState<UpdateCycle>(defaultCycle);
  const popupRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Reset on the open transition only, never on a data refresh mid-edit.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) setCycle(defaultCycle);
    prevOpenRef.current = open;
  }, [defaultCycle, open]);

  const handleStart = useCallback(() => {
    onStart({ cycle });
    onOpenChange(false);
  }, [cycle, onOpenChange, onStart]);

  const pickCycle = useCallback((key: string | null) => {
    const preset = key ? findFollowUpCyclePreset(key) : undefined;
    if (preset) setCycle(preset);
  }, []);

  const firstDue = format(add(new Date(), updateCycleDuration(cycle)), "EEE, MMM d");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        size="lg"
        initialFocus={popupRef}
        ref={popupRef}
        dialogOptions={{
          primary: { label: "Start Follow up", onClick: handleStart },
          // The date the pick commits the Member to. It remounts on change so
          // it fades in from the new value rather than snapping (`starting:`
          // is the enter transition; motion-reduce drops it).
          // (className: a fade-in on a keyed value — a recorded gap.)
          start: (
            <Box direction="row" align="center" gap="xs" minWidth={0}>
              <Text size="sm" variant="muted">
                First update
              </Text>
              <Text
                key={firstDue}
                size="sm"
                weight="medium"
                className="transition-opacity duration-200 starting:opacity-0 motion-reduce:transition-none"
              >
                {firstDue}
              </Text>
            </Box>
          ),
        }}
      >
        <DialogHeader>
          <Box direction="row" align="center" gap="sm">
            <Icon name={UPDATE_ICON} size="lg" color="muted" />
            <DialogTitle>Start Follow up</DialogTitle>
          </Box>
          <DialogDescription>
            {pastCount > 0
              ? `${START_FOLLOW_UP_DESCRIPTION} ${pastCount} past Follow up${pastCount === 1 ? "" : "s"} on record.`
              : START_FOLLOW_UP_DESCRIPTION}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <Box direction="column" gap="lg">
            {/* Not a Field: a Field labels its control, and a group's label
                would become every tab's accessible name. The label is a plain
                row on the field rung; each segment is named by its own word,
                with the "every" the label supplies. */}
            <Box gap="sm" fill="width">
              <Text
                size="xs"
                weight="medium"
                variant="muted"
                id="start-follow-up-cycle-label"
              >
                Repeat every
              </Text>
              {isMobile ? (
                <Select value={cycleKey(cycle)} onValueChange={pickCycle}>
                  <SelectTrigger
                    aria-labelledby="start-follow-up-cycle-label"
                    width="full"
                  >
                    <SelectValue>{formatUpdateCycle(cycle)}</SelectValue>
                  </SelectTrigger>
                  <SelectPopup>
                    {FOLLOW_UP_CYCLE_PRESETS.map((preset) => (
                      <SelectItem key={cycleKey(preset)} value={cycleKey(preset)}>
                        {formatUpdateCycle(preset)}
                      </SelectItem>
                    ))}
                  </SelectPopup>
                </Select>
              ) : (
                <Tabs
                  value={cycleKey(cycle)}
                  onValueChange={(value) => pickCycle(String(value))}
                >
                  <TabsList
                    aria-labelledby="start-follow-up-cycle-label"
                    fullWidth
                  >
                    {FOLLOW_UP_CYCLE_PRESETS.map((preset) => (
                      <TabsTab key={cycleKey(preset)} value={cycleKey(preset)}>
                        {formatUpdateCycleShort(preset)}
                      </TabsTab>
                    ))}
                  </TabsList>
                </Tabs>
              )}
            </Box>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
